import {ContactInfo, GenderType, SlimUser} from "./smm/smm-person.types";

export interface SMMCalendar {
    id: string;
    name: string;
}

export interface SMMAgendaCategory
{
    id: string; //ID of calendar that has this category
    calendarId: string,
    name: string,
    color: string,

}

export interface SMMCalendarSettings {
    id: string; //ID of settings
    calendar: string; //ID of calendar that has this settings
    hideWeekend: boolean;
    eventAnimation: boolean;
    showCanceledEvents: boolean;
    requireConfirmationAtReschedule: boolean;
    useDoctorContact: boolean;
}

export interface SMMCalendarFullSettings {
    calendarSettings: SMMCalendarSettings;
    agendaCategories: SMMAgendaCategory[];
}

export interface SMMCalendarDayWorkHours {
    id: string;
    calendar: string; //ID of calendar that has this working hours
    dayOfWeek: string;
    startTime: string;
    endTime: string;
    isWorkingDay: boolean;
}

export interface SMMCalendarDayWorkHoursList {
    calendarWorkingHoursDTOList:SMMCalendarDayWorkHours[];
}

export interface SMMCalendarEvent {
    id: string;
    title: string;
    startTime: string|number[];
    endTime: string|number[];
    userOwnerId: string;
    description: string;
    calendarEventType: CalendarEventType;
    address: string,
    roomName: string,
    calendars: string[];
    agendaCategories:string[];
    inhibitNotifications: boolean;
    eventResponse: CalendarEventResponse;
    seqId: string;
}

// Calendar event COMPACT mode
export interface SMMCalendarEventCompact {
    id: string,
    title: string,
    startDate: string|number[],
    endDate: string|number[],
    categoryColorCode: string,
    categoryLabel: string,
    patientFirstName: string,
    patientLastName: string
    userId: string,
    patientId: string,
    eventResponse: CalendarEventResponse;
    calendarEventType: CalendarEventType;
    seqId: string;
}

export interface SMMCalendarFullEvent{
    id: string;
    eventDTO: SMMCalendarEvent;
    agendaCategoryDTOList:SMMAgendaCategory[];
    eventOwnerName:string;
    firstInvitedUserName:string;
}

export enum CalendarEventResponse {
    NO_RESPONSE="NO_RESPONSE",
    USER_CONFIRMED="USER_CONFIRMED",
    CLINIC_CONFIRMED="CLINIC_CONFIRMED",
    USER_DECLINED = 'USER_DECLINED',
    USER_DECLINED_MOVED_AWAY = 'USER_DECLINED_MOVED_AWAY',
    USER_DECLINED_FINANCIAL_REASONS = 'USER_DECLINED_FINANCIAL_REASONS',
    USER_DECLINED_DENTAL_ANXIETY = 'USER_DECLINED_DENTAL_ANXIETY',
    USER_DECLINED_TOO_BUSY = 'USER_DECLINED_TOO_BUSY',
    USER_DECLINED_HEALTH_ISSUES = 'USER_DECLINED_HEALTH_ISSUES',
    USER_DECLINED_OTHER_DENTIST = 'USER_DECLINED_OTHER_DENTIST',
    USER_DECLINED_NO_LONGER_NEEDED = 'USER_DECLINED_NO_LONGER_NEEDED',
    CLINIC_DECLINED = 'CLINIC_DECLINED',
    CLINIC_DECLINED_MOVED_AWAY = 'CLINIC_DECLINED_MOVED_AWAY',
    CLINIC_DECLINED_FINANCIAL_REASONS = 'CLINIC_DECLINED_FINANCIAL_REASONS',
    CLINIC_DECLINED_DENTAL_ANXIETY = 'CLINIC_DECLINED_DENTAL_ANXIETY',
    CLINIC_DECLINED_TOO_BUSY = 'CLINIC_DECLINED_TOO_BUSY',
    CLINIC_DECLINED_HEALTH_ISSUES = 'CLINIC_DECLINED_HEALTH_ISSUES',
    CLINIC_DECLINED_OTHER_DENTIST = 'CLINIC_DECLINED_OTHER_DENTIST',
    CLINIC_DECLINED_NO_LONGER_NEEDED = 'CLINIC_DECLINED_NO_LONGER_NEEDED',
    PRESENT="PRESENT",
    ABSENT="ABSENT",
}

export class CalendarResponseDecoded{
    status: string;
    extendedStatus: string;
    reason: string;
    reasonPatientView: string;
    colorCls: string;
}

export function getReasonTextAndColorClass(rsp:CalendarEventResponse):CalendarResponseDecoded {
    const response = rsp ? rsp : CalendarEventResponse.NO_RESPONSE;
    const responseObj: CalendarResponseDecoded = {
        status: "Neconfirmat",
        extendedStatus: '',
        reason: '',
        reasonPatientView: '',
        colorCls: "bg-gray-500"
    };

    if (response.includes("DECLINED")) {
        responseObj.colorCls = "bg-red-500";
        responseObj.status = "Anulat";
        if (response.includes("USER_DECLINED")) {
            responseObj.extendedStatus = "Anulat automat de pacient";
        }
        if (response.includes("CLINIC_DECLINED")) {
            responseObj.extendedStatus = "Anulat manual de clinica";
        }
    }
    if (response.includes("CONFIRMED")) {
        responseObj.colorCls = "bg-green-200 border-green-500 border-2";
        responseObj.status = "Confirmat";
    }

    switch (response) {
        case CalendarEventResponse.USER_DECLINED:
        case CalendarEventResponse.CLINIC_DECLINED:
            responseObj.reason = "Motiv necunoscut";
            responseObj.reasonPatientView = "Altul";
            break;
        case CalendarEventResponse.USER_DECLINED_DENTAL_ANXIETY:
        case CalendarEventResponse.CLINIC_DECLINED_DENTAL_ANXIETY:
            responseObj.reason = "Anxietate dentara";
            break;
        case CalendarEventResponse.USER_DECLINED_FINANCIAL_REASONS:
        case CalendarEventResponse.CLINIC_DECLINED_FINANCIAL_REASONS:
            responseObj.reason = "Situatie financiara";
            break;
        case CalendarEventResponse.USER_DECLINED_HEALTH_ISSUES:
        case CalendarEventResponse.CLINIC_DECLINED_HEALTH_ISSUES:
            responseObj.reason = "Alte probleme de sanatate";
            break;
        case CalendarEventResponse.USER_DECLINED_MOVED_AWAY:
        case CalendarEventResponse.CLINIC_DECLINED_MOVED_AWAY:
            responseObj.reason = "Pacientul s-a mutat";
            responseObj.reasonPatientView = 'M-am mutat';
            break;
        case CalendarEventResponse.USER_DECLINED_NO_LONGER_NEEDED:
        case CalendarEventResponse.CLINIC_DECLINED_NO_LONGER_NEEDED:
            responseObj.reason = "Intervenția nu mai este necesara";
            break;
        case CalendarEventResponse.USER_DECLINED_TOO_BUSY:
        case CalendarEventResponse.CLINIC_DECLINED_TOO_BUSY:
            responseObj.reason = "Pacientul prea ocupat";
            responseObj.reasonPatientView = 'Nu am timp';
            break;
        case CalendarEventResponse.USER_DECLINED_OTHER_DENTIST:
        case CalendarEventResponse.CLINIC_DECLINED_OTHER_DENTIST:
            responseObj.reason = "A mers la alt dentist";
            responseObj.reasonPatientView = 'Am rezolvat cu alt dentist';
            break;

        case CalendarEventResponse.ABSENT:
            responseObj.status = "Neprezentare";
            responseObj.extendedStatus = "Pacientul nu s-a prezentat";
            responseObj.reason = "";
            responseObj.colorCls = "bg-red-500";
            break;
        case CalendarEventResponse.USER_CONFIRMED:
            responseObj.extendedStatus = "Confirmat de pacient, automat";
            break;
        case CalendarEventResponse.CLINIC_CONFIRMED:
            responseObj.extendedStatus = "Confirmat de clinica, manual";
            break;
        case CalendarEventResponse.PRESENT:
            responseObj.status = "Prezent";
            responseObj.extendedStatus = "Pacientul s-a prezentat";
            responseObj.reason = "";
            responseObj.colorCls = "bg-green-500";
            break;
        case CalendarEventResponse.NO_RESPONSE:
        default:
            responseObj.status = "Neconfirmat";
            responseObj.extendedStatus = "Fara informații de confirmare";
            responseObj.reason = "";
            responseObj.colorCls = "bg-gray-500";
    }

    if(responseObj.reasonPatientView === '') {
        //copy reasonPatientView if empty
        responseObj.reasonPatientView = responseObj.reason;
    }

    return responseObj;
}

export enum CalendarEventType{
    VISIT="VISIT",
    REMINDER="REMINDER",
    HOLIDAY="HOLIDAY",
    PUBLIC_HOLIDAY="PUBLIC_HOLIDAY",
    PARENTAL_LEAVE="PARENTAL_LEAVE",
    SABATICAL="SABATICAL",
    TRAININGS="TRAININGS",
    MEDICAL_LEAVE="MEDICAL_LEAVE",
    APPROVED_ABSENCE="APPROVED_ABSENCE"
}

export interface SMMUserCreationCalendarEvent {
    slimUserCreationDTO: SlimUser;
    eventDTO: SMMCalendarEvent;
}

export class TimeDiference{
    years:number;
    months:number;
    days:number;
    hours:number;
    minutes:number;
}


//------------------ INIT functions

export function getNewSMMCalendarEvent():SMMCalendarEvent{
    return {
        id: "",
        title: "",
        startTime: "",
        endTime: "",
        userOwnerId: "",
        address: "",
        roomName:"",
        description: "",
        calendarEventType: CalendarEventType.VISIT,
        calendars: [],
        agendaCategories:[],
        inhibitNotifications: false,
        eventResponse : CalendarEventResponse.NO_RESPONSE,
        seqId: ""
    }
}

export function getNewSMMUserCreationCalendarEvent(evt:SMMCalendarEvent):SMMUserCreationCalendarEvent{
    return {
        slimUserCreationDTO: {
            firstName: "",
            lastName: "",
            phoneNumber: "",
            gender: GenderType.MALE
        },
        eventDTO: evt
    }
}

export class RoomScheduleEvt {
    eventId:string;
    startTime: Date;
    endTime: Date;
    userOwnerId: string;
}

export class UserRoomInfo{
    fullName:string;
    userOwnerId: string;
    color: string;
}

export class ScheduleData{
    roomAvailability: Map<string, RoomScheduleEvt[]>;
    userRoomDetails: UserRoomInfo[];
    holidaysList: RoomScheduleEvt[];
}
